import Image from 'next/image';
import { useLocale, useTranslations } from 'next-intl';
import { useEffect } from 'react';
import { twMerge } from 'tailwind-merge';

import LocalizedPrice from '@/components/LocalizedPrice';
import { IMAGES_ASSETS_PREFIX } from '@/constants';
import type { ShippingMethodExtended } from '@/stores/cartStoreSlices/ShippingSlice';
import { useCheckoutStore } from '@/stores/useCheckoutStore';
import { Translated } from '@/templates/components/Translated';
import type { PossibleLocales } from '@/utils/AppConfig';

import { MultilineString } from '../../../home-components/MultilineString';
import SelectableOptionList from './SelectableOptionList'; // Import the reusable component

interface ShippingMethodCardProps {
  method: ShippingMethodExtended;
}
export const ShippingMethodCard: React.FC<ShippingMethodCardProps> = ({
  method,
}) => {
  const locale = useLocale() as PossibleLocales;

  return (
    <div className="flex w-full items-center gap-2 text-base">
      {method.image && (
        <Image
          src={`${IMAGES_ASSETS_PREFIX}/${method.image}`}
          className=""
          alt={method.nom || 'Payment'}
          width={75}
          height={25}
        />
      )}
      <div className="grow">
        <Translated fr={method.nom} en={method.nom_en} />
        <br />
        {method.delivery?.[locale] && (
          <span className="text-xs text-gray-500">
            {method.delivery[locale] || ''}
          </span>
        )}
        {method.additionalInformations?.[locale] && (
          <div className="text-xs text-gray-500">
            <MultilineString text={method.additionalInformations[locale]} />
          </div>
        )}
      </div>
      <div>
        <LocalizedPrice
          className={twMerge(
            'font-gothicb text-sm',
            method.price.offered ? 'text-primary-900' : '',
          )}
          free={method.price.amount === 0 && !method.price.offered}
          price={method.price.amount}
          offered={method?.price?.offered || false}
        />
      </div>
    </div>
  );
};

const ShippingMethods: React.FC = () => {
  const t = useTranslations('Checkout.SectionShipping');

  // Access store state and methods
  const {
    shippingMethods,
    selectedShippingMethodId,
    selectedBillingAddressId,
    selectedBillingAddress,
    selectedShippingAddress,
    fetchShippingMethods,
    selectShippingMethod,
    loadingShipping,
    totalDetails: { totalPanier },
    loading,
    error,
  } = useCheckoutStore();

  // Fetch shipping methods when session is available
  useEffect(() => {
    if (selectedBillingAddress || selectedShippingAddress) {
      fetchShippingMethods();
    }
  }, [
    fetchShippingMethods,
    selectedShippingAddress,
    selectedBillingAddress,
    totalPanier,
  ]);

  // If loading, show a loading message
  if (loading.shippingMethods)
    return <p>{t('Messages.CHARGEMENT_METHODE_EN_COURS')}</p>;

  // If there's an error, display the error message
  if (error.shippingMethods)
    return <p className="alert alert-error">{error.shippingMethods}</p>;
  if (loadingShipping) {
    return (
      <div className="loading loading-spinner mx-auto block bg-primary-900" />
    );
  }
  return selectedBillingAddress || selectedShippingAddress ? (
    <SelectableOptionList
      canUnselect
      idField="id"
      options={shippingMethods}
      selectedOptionId={selectedShippingMethodId || selectedBillingAddressId}
      onSelect={(opt) => selectShippingMethod(opt.id!)}
      type="shipping"
      renderOption={(method) => <ShippingMethodCard method={method} />}
    />
  ) : (
    <>{t('ChoixDuTransporteur.message_si_aucune_adresse_selectionnee')}</>
  );
};

export default ShippingMethods;
